import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Calendar, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Compact admin widget showing the next auto-run + a "Run now" button.
 *  Hidden gracefully if the user is not admin (403 from /scheduler/status). */
export default function SchedulerControls() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [allowed, setAllowed] = useState(true);

  const fetchStatus = () => {
    axios.get(`${API}/scheduler/status`, { withCredentials: true })
      .then(({ data }) => setStatus(data))
      .catch((e) => {
        if (e?.response?.status === 403) setAllowed(false);
      });
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleRunNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/scheduler/run-keyword-tracking-now?force=true`,
        {},
        { withCredentials: true }
      );
      if (data.fatal_error) {
        toast.error(`Erreur fatale : ${data.fatal_error}`);
      } else {
        toast.success(
          `Tracking terminé : ${data.tracked_ok}/${data.keywords_total} mots-clés actualisés ` +
          `sur ${data.clients} clients en ${data.duration_seconds}s` +
          (data.errors > 0 ? ` (${data.errors} erreurs)` : '')
        );
        // Hint user to reload the page so the new history shows up
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (e) {
      toast.error('Erreur — vous devez être admin pour déclencher cette action');
    } finally {
      setRunning(false);
    }
  };

  if (!allowed) return null;

  const ktJob = status?.jobs?.find(j => j.id === 'weekly_keyword_tracking');
  const nextRun = ktJob?.next_run_time
    ? new Date(ktJob.next_run_time).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <div className="flex items-center gap-2 text-neutral-600">
        <Calendar className="w-4 h-4" />
        {status?.running ? (
          <>
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span>Auto-tracking actif</span>
            {nextRun && <span className="text-neutral-400">· prochain : {nextRun}</span>}
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span>Scheduler arrêté</span>
          </>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRunNow}
        disabled={running}
        data-testid="scheduler-run-now-btn"
        className="ml-auto"
      >
        <RefreshCw className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
        {running ? 'En cours...' : 'Lancer maintenant'}
      </Button>
    </div>
  );
}
